import {
  QuizConceptSummary,
  QuizProgressStatus,
  QuizQuestion,
  QuizQuestionAnswer,
  QuizResultRow
} from './quiz.types'

export const getCurrentQuestionNo = (
  questionIndex: number,
  totalQuestions: number
): number => Math.min(questionIndex + 1, totalQuestions)

export const getAnsweredQuestionCount = (
  answers: Record<string, QuizQuestionAnswer>
): number => Object.values(answers).length

export const getCorrectFirstAttemptCount = (
  answers: Record<string, QuizQuestionAnswer>
): number =>
  Object.values(answers).filter(answer => answer.firstAttemptCorrect).length

export const getPercentageScore = (
  value: number,
  total: number,
  defaultValue = 0
): number => {
  if (!total) {
    return defaultValue
  }

  return Number(((value / total) * 100).toFixed(1))
}

export const getProgressStatuses = (
  questions: QuizQuestion[],
  answers: Record<string, QuizQuestionAnswer>
): QuizProgressStatus[] =>
  questions.map(question => {
    const answer = answers[question.id]

    if (!answer) {
      return 'unanswered'
    }

    return answer.firstAttemptCorrect ? 'correct' : 'incorrect'
  })

const getOptionTextById = (
  options: QuizQuestion['options'],
  optionId: string
): string => options.find(option => option.id === optionId)?.text || ''

export const getResultsRows = (
  questions: QuizQuestion[],
  answers: Record<string, QuizQuestionAnswer>
): QuizResultRow[] =>
  questions.map(question => {
    const answer = answers[question.id]
    const correctOption = question.options.find(
      option => option.id === question.correctOptionId
    )
    const userAnswer = answer
      ? getOptionTextById(question.options, answer.firstSelectedOptionId)
      : ''

    return {
      questionId: question.id,
      prompt: question.prompt,
      correctAnswer: correctOption?.text || '',
      userAnswer,
      isCorrect: Boolean(answer?.firstAttemptCorrect),
      explanation: question.explanation
    }
  })

export const getConceptSummary = (
  questions: QuizQuestion[],
  answers: Record<string, QuizQuestionAnswer>
): QuizConceptSummary => {
  const strongConcepts = new Set<string>()
  const weakConcepts = new Set<string>()

  questions.forEach(question => {
    const answer = answers[question.id]

    if (!answer || !answer.isResolved) {
      return
    }

    if (answer.firstAttemptCorrect) {
      strongConcepts.add(question.concept)
    } else {
      weakConcepts.add(question.concept)
    }
  })

  return {
    strongConcepts: Array.from(strongConcepts).slice(0, 3),
    weakConcepts: Array.from(weakConcepts).slice(0, 7)
  }
}

/**
 * Picks the result-screen feedback copy based on the quiz percentage.
 * Bands match the spaced-repetition score ranges shown to the student.
 */
export const getResultFeedbackTranslationKey = (percentage: number): string => {
  if (percentage < 50) {
    return 'aiSpaceRepetition.resultFeedback0to49'
  }

  if (percentage < 60) {
    return 'aiSpaceRepetition.resultFeedback50to59'
  }

  if (percentage < 70) {
    return 'aiSpaceRepetition.resultFeedback60to69'
  }

  if (percentage < 80) {
    return 'aiSpaceRepetition.resultFeedback70to79'
  }

  if (percentage < 90) {
    return 'aiSpaceRepetition.resultFeedback80to89'
  }

  if (percentage < 100) {
    return 'aiSpaceRepetition.resultFeedback90to99'
  }

  return 'aiSpaceRepetition.resultFeedback100'
}
